/**
 * shopAdapter — la boutique de gestes : les produits (`staticData.shop_products`), les achats et les
 * pétales du compte (`currentUser.object`), et l'accusé d'un achat.
 *
 * Port de `student/shop/MyPetalsTabScreen.js` et de ses pièces :
 *  - les gestes sont les produits de catégorie `gesture`, dans l'ordre du serveur ; leur image est
 *    `icon_path`, servie par le serveur ; « Acheté n fois » compte `shop_purchases` du produit
 *    (`ShopProductListItem`) ;
 *  - la phrase de Tina compare les pétales à 12 ;
 *  - l'accusé reprend les écrans de félicitations poussés après un achat : le mot du geste que Tina
 *    dit dans sa bulle (`SHOP_PRODUCT_PURCHASED`, `congrats_message`), le total
 *    (`ShareStatsCongrats`), la quête de Tina (`MY_PROGRESS`, la quête 4 de la réponse, qui avance
 *    depuis son compte d'avant), le titre qu'elle vient de donner (`NEW_TITLE_CONGRATS`, sous la
 *    condition de `MyPetalsTabScreen`).
 */
import { type ApiGameSkill, type ApiGameSkillSet, type ApiGameStatistics, toUIQuest } from "../shared/questAdapter"
import {
    type UIShopGesture,
    type UIShopItem,
    type UIShopNewTitle,
    type UIShopPurchaseOutcome,
} from "../../../ui/types/studentGame"

export type ApiShopProduct = {
    id?: number
    name?: string | null
    category?: string | null
    petals_value?: number | null
    icon_path?: string | null
    short_description?: string | null
    description?: string | null
    /** Le mot de Tina après l'achat (`ShopProductResource`). */
    congrats_message?: string | null
}

export type ApiShopPurchase = { shop_product_id?: number | null }

export type ApiShopAccount = {
    petals?: number | null
    student_title?: string | null
    title_skill_set_id?: number | null
    shop_purchases?: ApiShopPurchase[] | null
    statistics?: ApiGameStatistics
}

/** La quête que les gestes font avancer : celle de Tina (`findItemInListWithId(4, …)`, `PetalLog`). */
export const GESTURES_SKILL_SET_ID = 4

/** Une quête telle que la réponse de l'achat la sert : avec son compte d'avant (`SkillSetResource`). */
export type ApiShopSkillSet = ApiGameSkillSet & { previous_count?: number | null }

/** La réponse de l'achat (`all_data`) : le compte sous `data`, et les quêtes de l'élève à côté. */
export type ApiPurchaseResponse = { skill_sets?: ApiShopSkillSet[] | null } | null | undefined

/** La quête de Tina dans la réponse (`findItemInListWithId(4, allData?.skill_sets)`). */
export function gesturesSkillSetOf(response: ApiPurchaseResponse): ApiShopSkillSet | null {
    return response?.skill_sets?.find((skillSet) => skillSet?.id === GESTURES_SKILL_SET_ID) ?? null
}

/** Sous ce solde, Tina dit comment gagner des pétales (`MyPetalsTabScreen.js`). */
const HINT_PETALS_THRESHOLD = 12

const HINTS = {
    earnMore: "Termine de nouvelles quêtes pour gagner plus de pétales et acheter des gestes !",
    choose: "Choisis un geste pour faire fructifier tes efforts !",
}

/** Un geste. Sur une carte de la liste, le texte long ne se montre pas : il vit dans la fiche. */
export function toUIShopGesture(
    product: ApiShopProduct | null | undefined,
    withDescription: boolean
): UIShopGesture | null {
    if (product?.id == null) return null
    return {
        id: product.id,
        name: product.name ?? "",
        shortDescription: product.short_description ?? "",
        description: withDescription ? (product.description ?? null) : null,
        ...(withDescription ? { congratsMessage: product.congrats_message ?? null } : {}),
        imageUrl: product.icon_path ?? null,
        price: product.petals_value ?? 0,
    }
}

export const gestureProducts = (products: ApiShopProduct[] | null | undefined): ApiShopProduct[] =>
    (products ?? []).filter((product) => product?.category === "gesture")

export const purchaseCount = (purchases: ApiShopPurchase[] | null | undefined, productId: number): number =>
    (purchases ?? []).filter((purchase) => purchase?.shop_product_id === productId).length

/** Les gestes de la boutique. `null` tant que les produits ne sont pas là. */
export function toUIShopItems(
    products: ApiShopProduct[] | null | undefined,
    purchases: ApiShopPurchase[] | null | undefined
): UIShopItem[] | null {
    if (products == null) return null
    return gestureProducts(products)
        .map((product): UIShopItem | null => {
            const gesture = toUIShopGesture(product, false)
            return gesture == null ? null : { gesture, purchasedCount: purchaseCount(purchases, gesture.id) }
        })
        .filter((item): item is UIShopItem => item != null)
}

/** La phrase de Tina (`MyPetalsTabScreen.js` : `petals < 12`). Sans solde servi : « Choisis… », comme avant. */
export function shopHint(petals: number | null | undefined): string {
    return petals != null && petals < HINT_PETALS_THRESHOLD ? HINTS.earnMore : HINTS.choose
}

/**
 * « Tu as planté en tout 4 arbres grâce à tes efforts ! » — `ShareStatsCongrats.js`, à la lettre :
 * le repas se reconnaît à son nom (« Offrir un repas »), tout autre geste se dit en arbres ;
 * « en tout » à partir de deux.
 */
export function purchaseTotalLabel(gestureName: string, count: number): string {
    const inAll = count > 1 ? "en tout " : ""
    if (gestureName === "Offrir un repas") return `Tu as offert ${inAll}${count} repas grâce à tes efforts !`
    return `Tu as planté ${inAll}${count} ${count > 1 ? "arbres" : "arbre"} grâce à tes efforts !`
}

/**
 * La condition de `MyPetalsTabScreen.js`, à la lettre, pour pousser `NEW_TITLE_CONGRATS` : l'étape
 * vient de changer (compte à zéro, évolution pas encore lue) et le titre de l'étape franchie n'est
 * pas celui de la précédente. L'écran de félicitations se mesurait sous ce nom (`congrats.*`).
 */
export function announcesNewTitle(skillSet: ApiGameSkillSet | null | undefined): boolean {
    const steps = skillSet?.steps ?? []
    const currentStep = skillSet?.current_step ?? 0
    return (
        skillSet?.current_count === 0 &&
        !skillSet.is_last_evolution_read &&
        currentStep >= 2 &&
        steps[currentStep - 1]?.title_gained !== steps[currentStep - 2]?.title_gained
    )
}

/**
 * Le compte d'où la quête de l'accusé part — `SkillItem.js` avec son animation : tant que l'évolution
 * n'est pas lue, `previous_count`. `null` quand il n'y a rien à animer (évolution lue, compte d'avant
 * absent ou pas en deçà du compte montré).
 */
export function questFromCount(skillSet: ApiShopSkillSet | null | undefined, shownCount: number | null): number | null {
    if (skillSet == null || skillSet.is_last_evolution_read || shownCount == null) return null
    const previous = skillSet.previous_count
    return typeof previous === "number" && previous >= 0 && previous < shownCount ? previous : null
}

/**
 * Le titre que l'achat vient de donner — la condition de `MyPetalsTabScreen.js` pour pousser
 * `NEW_TITLE_CONGRATS` : l'étape vient de changer (compte à zéro, évolution pas encore lue), et le
 * titre de l'étape franchie n'est pas celui de la précédente.
 *
 * Le titre annoncé est celui que l'étape franchie donne (`steps[current_step - 1]`), celui que le
 * serveur pose comme titre de l'élève (`StudentTrait::getStudentTitle`) et dont l'ancien écran
 * montrait déjà l'icône. L'ancien écran en écrivait le NOM depuis `steps[current_step]` : en arrivant
 * à l'étape 2 de la quête de Tina, il annonçait le titre suivant. Ce bug n'est pas reproduit.
 */
export function toUIShopNewTitle(
    skillSet: ApiGameSkillSet | null | undefined,
    account: ApiShopAccount | null | undefined
): UIShopNewTitle | null {
    if (skillSet?.id == null || !announcesNewTitle(skillSet)) return null
    const steps = skillSet.steps ?? []
    const gained = steps[(skillSet.current_step ?? 0) - 1]?.title_gained
    if (gained == null || gained === "") return null
    const applied = String(account?.title_skill_set_id ?? 1) === String(skillSet.id)
    return { title: gained, applied, currentTitle: applied ? null : (account?.student_title ?? null) }
}

/**
 * L'accusé d'un achat : le total du geste (les achats du compte, que la réponse de l'achat a déjà
 * remplacés), la quête de Tina telle que la réponse la sert (`skill_sets` de la réponse, lue avec
 * l'étape tout juste finie), et le titre qu'elle vient de donner.
 */
export function toUIShopPurchaseOutcome(
    gesture: UIShopGesture,
    account: ApiShopAccount | null | undefined,
    gesturesSkillSet: ApiShopSkillSet | null | undefined,
    skills: ApiGameSkill[] | null | undefined
): UIShopPurchaseOutcome {
    const quest = toUIQuest(gesturesSkillSet, skills, account?.statistics, { showLastEvolution: true })
    const message = gesture.congratsMessage?.trim()
    return {
        totalLabel: purchaseTotalLabel(gesture.name, purchaseCount(account?.shop_purchases, gesture.id)),
        quest,
        questFromCount: questFromCount(gesturesSkillSet, quest?.count ?? null),
        newTitle: toUIShopNewTitle(gesturesSkillSet, account),
        congratsMessage: message != null && message !== "" ? message : null,
    }
}
